import { create } from 'zustand';
import { log } from '../log/log';
import type { MessageEnvelope, PersistenceAdapter } from './syncEngine';

interface PersistedMessage {
  id: string;
  conversationId: string;
  epoch: number;
  serverSeq: number;
  contentHash: string;
  senderAccountId: string;
  body?: string | undefined;
  sentAt: number;
}

interface PersistedConversation {
  id: string;
  kind: 'direct' | 'group';
  name?: string;
  lastMessageAt?: number;
  createdAt: number;
}

interface MacOSPersistenceState {
  messages: Map<string, PersistedMessage>;
  conversations: Map<string, PersistedConversation>;
  addMessage: (message: PersistedMessage) => void;
  addMessages: (messages: PersistedMessage[]) => void;
  updateConversation: (conversationId: string, lastMessageAt: number) => void;
}

export const useMacOSPersistenceStore = create<MacOSPersistenceState>((set) => ({
  messages: new Map(),
  conversations: new Map(),

  addMessage: (message) => {
    set((state) => {
      const newMessages = new Map(state.messages);
      newMessages.set(message.contentHash, message);
      return { messages: newMessages };
    });
  },

  addMessages: (messages) => {
    set((state) => {
      const newMessages = new Map(state.messages);
      for (const message of messages) {
        newMessages.set(message.contentHash, message);
      }
      return { messages: newMessages };
    });
  },

  updateConversation: (conversationId, lastMessageAt) => {
    set((state) => {
      const newConversations = new Map(state.conversations);
      const existing = state.conversations.get(conversationId);

      const conversation: PersistedConversation = existing
        ? { ...existing, lastMessageAt }
        : {
            id: conversationId,
            kind: 'direct',
            lastMessageAt,
            createdAt: Date.now(),
          };

      newConversations.set(conversationId, conversation);
      return { conversations: newConversations };
    });
  },
}));

export class MacOSPersistenceAdapter implements PersistenceAdapter {
  async writeMessage(envelope: MessageEnvelope): Promise<void> {
    useMacOSPersistenceStore.getState().addMessage(toMessage(envelope));

    log.debug('persistence.macos.writeMessage written', {
      contentHash: envelope.contentHash.slice(0, 8),
    });
  }

  async writeBatch(envelopes: MessageEnvelope[]): Promise<void> {
    useMacOSPersistenceStore.getState().addMessages(envelopes.map(toMessage));

    log.debug('persistence.macos.writeBatch complete', { count: envelopes.length });
  }

  async updateConversation(conversationId: string, lastMessageAt: number): Promise<void> {
    useMacOSPersistenceStore.getState().updateConversation(conversationId, lastMessageAt);
  }
}

function toMessage(envelope: MessageEnvelope): PersistedMessage {
  const message: PersistedMessage = {
    id: envelope.contentHash,
    conversationId: envelope.conversationId,
    epoch: envelope.epoch,
    serverSeq: envelope.serverSeq,
    contentHash: envelope.contentHash,
    senderAccountId: envelope.senderAccountId,
    sentAt: envelope.sentAt,
  };

  if (envelope.body !== undefined) {
    message.body = envelope.body;
  }

  return message;
}
